import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { store } from '../store';
import { authApi } from '../services/authApi';

const AuthContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL}`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('finai-token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData, hasPlan } = response.data;
    localStorage.setItem('finai-token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser({ ...userData, hasPlan });
    return { ...userData, hasPlan };
  };

  const register = async (name, email, password, company, plan = null) => {
    const response = await axios.post(`${API}/auth/register`, { 
      name, 
      email, 
      password, 
      company,
      plan
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('finai-token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const updatePlan = async (plan) => {
    const response = await store.dispatch(
      authApi.endpoints.updatePlan.initiate(plan)
    ).unwrap();
    setUser(prev => ({ ...prev, plan }));
    return response;
  };

  const logout = () => {
    localStorage.removeItem('finai-token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, updatePlan }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
